import { useDocumentVisibility, useRafInterval } from 'ahooks';
import { FC, memo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { messageOnError } from '@/common/utils';

import { SearchConsoleAccountsQueryKeys, useGetSearchConsoleAccountOTPQuery, useRemoveSearchConsoleAccountOTPMutation } from '@/modules/DomainsApi';

import { useIsSuccessful } from '@/common/hooks';
import { SearchConsoleAccountOTPAuthenticatorView } from './SearchConsoleAccountOTPAuthenticatorView';

interface Props {
  searchConsoleAccountId: string;
  refreshInterval: number;
}

export const SearchConsoleAccountOTPAuthenticatorContainer: FC<Props> = memo(({
  searchConsoleAccountId,
  refreshInterval,
}) => {
  const queryClient = useQueryClient();
  const appMessageControls = AppMessage.useControls();
  const [tokenVisible, setTokenVisible] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const documentVisibility = useDocumentVisibility();

  const isSearchConsoleAccountRequestSuccess = useIsSuccessful({ queryKey: [SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId] });

  const { data: generatedCode, isSuccess: isTokenReceived, isFetching: isTokenFetching, refetch } = useGetSearchConsoleAccountOTPQuery(searchConsoleAccountId)({
    enabled: isSearchConsoleAccountRequestSuccess && tokenVisible && documentVisibility === 'visible',
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchInterval,
    onSuccess: () => {
      const time = refreshInterval - (Math.floor(Date.now() / 1000) % refreshInterval);
      setRemainingTime(time);
      setRefetchInterval(time * 1000);
    },
    onError: (error) => {
      messageOnError(error, appMessageControls);

      handleSetTokenUnvissible();
    }
  });

  const {
    mutate: removeOTPMutation,
    isLoading: isRemoveOTPLoading
  } = useRemoveSearchConsoleAccountOTPMutation(searchConsoleAccountId)({
    onSuccess: async () => {
      appMessageControls.success('OTP токен успішно видалено!');

      queryClient.setQueryData(
        [SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId],
        (data?: object) => {
          return { ...data, isOtpSetted: false, otpAthenticator: null };
        });

      await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll], {
        exact: false,
      });

    },
    onError: (error) => {
      messageOnError(error, appMessageControls);
    }
  });

  const handleSetTokenVissible = () => {
    setTokenVisible(true);
    refetch();
  };

  const handleSetTokenUnvissible = () => {
    setTokenVisible(false);
  };

  useRafInterval(() => {
    setRemainingTime((prevTime) => (prevTime - 1));
  }, (tokenVisible && !isTokenFetching && isTokenReceived) ? 1000 : -1);

  const handleRemoveOTP = () => {
    removeOTPMutation();
  };

  return (
    <SearchConsoleAccountOTPAuthenticatorView
      refreshInterval={ refreshInterval }
      remainingTime={ remainingTime }
      generatedCode={ generatedCode || '' }
      isTokenVisible={ tokenVisible }
      isTokenFetching={ isTokenFetching }
      isTokenReceived={ isTokenReceived }
      isRemoveOTPLoading={ isRemoveOTPLoading }
      handleSetTokenVissible={ handleSetTokenVissible }
      handleSetTokenUnvissible={ handleSetTokenUnvissible }
      handleRemoveOTP={ handleRemoveOTP }
    />
  );
});
